
import React, { useCallback } from 'react';
import BulletinCard from './BulletinCard';
import DropAnnouncementCard from '@/components/drops/DropAnnouncementCard';
import BulletinsInfiniteLoader from './BulletinsInfiniteLoader';
import { useBulletinGridLayout } from '@/hooks/use-bulletin-grid-layout';

interface BulletinsGridProps {
  bulletins: any[];
  onLike: (id: string) => void;
  onDelete: () => void;
  activeTab: string;
  onLoadMore: () => void;
  isLoading: boolean;
  hasNextPage: boolean;
  onBulletinClick?: (id: string) => void;
  selectedBulletinId?: string;
}

/**
 * Component for rendering a grid of bulletins
 * Implements virtualized bulletins list with infinite loading
 */
const BulletinsGrid = ({
  bulletins,
  onLike,
  onDelete,
  activeTab,
  onLoadMore,
  isLoading,
  hasNextPage,
  onBulletinClick,
  selectedBulletinId
}: BulletinsGridProps) => {
  // Removed excessive logging for performance

  const { gridItems, getColumnClass, isMobile } = useBulletinGridLayout({
    bulletins,
    squarePercentage: 67,  // 67% squares (2/3)
    horizontalPercentage: 16,  // 16% horizontal
    largePercentage: 17   // 17% large
  });
  
  const handleBulletinClick = useCallback((id: string) => {
    if (onBulletinClick) {
      onBulletinClick(id);
    }
  }, [onBulletinClick]);

  if (bulletins.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <h3 className="text-lg font-medium text-muted-foreground">No bulletins found</h3>
        <p className="text-sm text-muted-foreground mt-2">Be the first to post!</p>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div 
        className={`grid ${getColumnClass()} auto-rows-auto min-w-0 max-w-full`}
        style={{ 
          contain: 'layout',
          transform: 'translate3d(0,0,0)',
          willChange: 'transform'
        }}
      >
        {bulletins.map((bulletin: any, index: number) => {
          const gridItem = gridItems.find((item) => item.id === bulletin.id);
          const spans = gridItem?.spans || {
            row: 1,
            col: 1,
            isHorizontal: false,
          };

          return (
            <div
              key={bulletin.id}
              className={`
                ${spans.row === 2 ? "row-span-2" : ""} 
                ${spans.col === 2 ? "col-span-2" : ""} 
                w-full min-w-0 max-w-full overflow-hidden
              `}
              style={{
                aspectRatio: spans.isHorizontal 
                  ? "2/1" 
                  : spans.row === 2 
                    ? "1/2" 
                    : "1/1",
                contain: 'layout style'
              }}
            >
              <div className="h-full w-full">
                {bulletin?.is_drop_announcement && bulletin?.drop_collection_id ? (
                  <DropAnnouncementCard
                    bulletin={bulletin}
                    onClaim={(collectionId) => {
                      // Navigate to the collection detail for claim flow
                      window.location.href = `/drops/${collectionId}`;
                    }}
                  />
                ) : (
                  <BulletinCard
                    bulletin={bulletin}
                    onLike={() => onLike(bulletin.id)}
                    onDelete={onDelete}
                    size={
                      spans.isHorizontal
                        ? "horizontal"
                        : spans.row > 1
                          ? "medium"
                          : "small"
                    }
                    isSelected={bulletin.id === selectedBulletinId}
                    onClose={() => handleBulletinClick(bulletin.id)}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Infinite loading component */}
      <BulletinsInfiniteLoader
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        onLoadMore={onLoadMore}
      />
    </div>
  );
};

export default BulletinsGrid;
